import { Injectable, NgZone } from '@angular/core';
import { environment } from "../../../environments/environment";
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Division } from "../models/division.model";
import { RingGroup } from "../models/ringgroup.model";
import { Ring } from "../models/rings.model";
import { RingSession } from "../models/ringsession.model";
import { DivisionParticipant } from "../models/division-participant.model";
import { SendNotificationDto } from "../models/notification.model";
import { SubEvent } from "../models/sub-event.model";
import { RequestCacheService } from "./request-cache.service";
import { BYPASS_403_ERROR } from "../http-interceptor/auth.interceptor";

export type CopyRingsOption = 'NONE' | 'REPLACE' | 'MERGE' | 'KEEP_BOTH';

export interface CopyEventRingsRequest {
  srcEventId: number;
  dstEventId: number;
  ringsOption: CopyRingsOption;
  groupsOption: CopyRingsOption;
}

@Injectable({
  providedIn: "root",
})
export class EventService {
  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private toaster: ToastrService,
    private requestCache: RequestCacheService
  ) {}

  public getUserEvents(): Observable<any> {
    return this.http.get(`${environment.APIURL}api/events/my`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventRoles(eventId: number): Observable<any> {
    return this.requestCache.get(`${environment.APIURL}api/events/${eventId}/roles`, undefined, 600000).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventConfig(eventId: number): Observable<any> {
    return this.requestCache.get(`${environment.APIURL}api/events/${eventId}/config/options`, undefined, 600000).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventStaffMap(eventId: number): Observable<any> {
    return this.requestCache.get(`${environment.APIURL}api/events/${eventId}/staff/list`, undefined, 600000).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public updateEventConfigItemPublicDraws(eventId: number, enable: boolean): Observable<any> {
    let enabled = enable ? "enabled" : "disabled";
    return this.http.put(`${environment.APIURL}api/events/${eventId}/config/public-draws/${enabled}`, '').pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public updateEventConfigItemPublicTimeTable(eventId: number, enable: boolean): Observable<any> {
    let enabled = enable ? "enabled" : "disabled";
    return this.http.put(`${environment.APIURL}api/events/${eventId}/config/public-timetable/${enabled}`, '').pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public sendAlert(eventId: number, data: any): Observable<any> {
    return this.http.put(`${environment.APIURL}api/events/${eventId}/notification/send-alert`, data);
  }

  public callMedic(eventId: number, data: any): Observable<any> {
    return this.http.put(`${environment.APIURL}api/events/${eventId}/notification/call-medic`, data).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventsAssignedToJudgeUser(): Observable<any> {
    return this.http.get(`${environment.APIURL}api/events/judge`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventJudgeRings(eventId: number): Observable<any> {
    return this.http.get(`${environment.APIURL}api/events/${eventId}/judge/rings`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public createEventRing(eventId: number, ring: Ring): Observable<Ring> {
    this.requestCache.invalidateAllForUrlParts([`api/events/${eventId}/rings`]);
    return this.http.post<Ring>(`${environment.APIURL}api/events/${eventId}/rings`, ring).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public updateEventRing(eventId: number, ring: Ring): Observable<Ring> {
    this.requestCache.invalidateAllForUrlParts([`api/events/${eventId}/rings`]);
    return this.http.put<Ring>(`${environment.APIURL}api/events/${eventId}/rings`, ring).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public createEventRings(eventId: number, rings: any[]): Observable<Ring> {
    this.requestCache.invalidateAllForUrlParts([`api/events/${eventId}/rings`]);
    return this.http.post<Ring>(`${environment.APIURL}api/events/${eventId}/rings/bulk`, rings).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventRings(eventId: number): Observable<Ring[]> {
    return this.requestCache.get<Ring[]>(`${environment.APIURL}api/events/${eventId}/rings`, undefined, 600000).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventRingsForSubEvent(eventId: number, subEventId: number ): Observable<Ring[]> {
    return this.requestCache.get<Ring[]>(`${environment.APIURL}api/events/${eventId}/rings/subevent/${subEventId}`, undefined, 600000).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getRingStatistics(eventId: number, ringIds?: number[]): Observable<any[]> {
    const payload: any = {
      excludeIds: [0],
      eventId: eventId
    };
    if (ringIds) {
      payload.ringIds = ringIds;
    }
    return this.http.put<any[]>(`${environment.ANALYTICIS_API_URL}api/statistics/rings/public`, payload).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public rebuildRingStatistics(ringId: number): Observable<any> {
    return this.http.put<any>(`${environment.ANALYTICIS_API_URL}api/statistics/rings/ring/${ringId}/rebuild`, {}).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public createEventRingGroup(eventId: number, group: RingGroup): Observable<RingGroup> {
    return this.http.post<RingGroup>(`${environment.APIURL}api/events/${eventId}/rings/group`, group).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventRingGroups(eventId: number): Observable<RingGroup[]> {
    return this.http.get(`${environment.APIURL}api/events/${eventId}/rings/groups`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public updateEventRingGroups(eventId: number, groups: RingGroup[]): Observable<RingGroup[]> {
    return this.http.put<RingGroup[]>(`${environment.APIURL}api/events/${eventId}/rings/groups`, groups).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  // Copy areas calls: a 403 means no access to that event, so skip the interceptor's sign-in redirect
  // and let the dialog handle it

  public getEventRingsForCopy(eventId: number): Observable<Ring[]> {
    return this.http.get<Ring[]>(`${environment.APIURL}api/events/${eventId}/rings`, {
      context: new HttpContext().set(BYPASS_403_ERROR, true)
    });
  }

  public getEventRingGroupsForCopy(eventId: number): Observable<RingGroup[]> {
    return this.http.get<RingGroup[]>(`${environment.APIURL}api/events/${eventId}/rings/groups`, {
      context: new HttpContext().set(BYPASS_403_ERROR, true)
    });
  }

  public updateEventRingGroupsForCopy(eventId: number, groups: RingGroup[]): Observable<RingGroup[]> {
    return this.http.put<RingGroup[]>(`${environment.APIURL}api/events/${eventId}/rings/groups`, groups, {
      context: new HttpContext().set(BYPASS_403_ERROR, true)
    });
  }

  public copyEventRings(eventId: number, payload: CopyEventRingsRequest): Observable<any> {
    this.requestCache.invalidateAllForUrlParts([`api/events/${eventId}/rings`]);
    return this.http.post<any>(`${environment.APIURL}api/events/${eventId}/rings/copy`, payload, {
      context: new HttpContext().set(BYPASS_403_ERROR, true)
    });
  }

  removeEventRingGroup(eventId: number, groupId: number): Observable<boolean> {
    return this.http.delete(`${environment.APIURL}api/events/${eventId}/rings/group/${groupId}`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  removeEventRingAssignment(eventId: number, ringId: number): Observable<boolean> {
    return this.http.delete(`${environment.APIURL}api/events/${eventId}/rings/${ringId}/assignment`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  removeEventRing(eventId: number, ringId: number): Observable<boolean> {
    this.requestCache.invalidateAllForUrlParts([`api/events/${eventId}/rings`]);
    return this.http.delete(`${environment.APIURL}api/events/${eventId}/rings/${ringId}`).pipe(
      catchError(this.handleError),
      map((res) => res)
    );
  }

  public getEventRingSessions(eventId: number, ringId: number): Observable<RingSession[]> {
    return this.http.get<RingSession[]>(`${environment.APIURL}api/events/${eventId}/rings/${ringId}/sessions`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getEventSessions(eventId: number): Observable<RingSession[]> {
    return this.http.get<RingSession[]>(`${environment.APIURL}api/events/${eventId}/rings/sessions`);
  }

  public getDivisions(eventId: number): Observable<Division[]> {
    return this.http.get<Division[]>(`${environment.APIURL}api/event/divisions/${eventId}/allowed`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getDivisionsForEventRing(eventId: number, ringId: number): Observable<Division[]> {
    return this.http.get<Division[]>(`${environment.APIURL}api/event/divisions/${eventId}/ring/${ringId}`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getDivisionParticipants(divisionId: number): Observable<DivisionParticipant[]> {
    return this.http.get<DivisionParticipant[]>(`${environment.APIURL}api/event/divisions/${divisionId}/participants`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getDivisionParticipant(divisionId: number, personId: number): Observable<DivisionParticipant> {
    return this.http.get<DivisionParticipant>(`${environment.APIURL}api/event/divisions/${divisionId}/participant/${personId}`);
  }

  public getSparringBracket(ringDivisionId: number, poolNo: number | null): Observable<any[]> {
    let queryParams: any = {};
    if (poolNo != null) {
      queryParams.poolNo = poolNo;
    }

    return this.http.get<any[]>(`${environment.APIURL}api/event/divisions/${ringDivisionId}/bracket/sparring`, {
      params: queryParams,
    }).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getFlowsSparring(eventId: number, ringId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.APIURL}api/events/${eventId}/rings/${ringId}/flows-sparring`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getTrampolineTumblingDivisionTypes(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.APIURL}api/event/divisions/types/trampoline-tumbling`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getDivisionsWithCompetitors(eventId: number, competitors: any[]): Observable<Division[]> {
    const params = {
      competitors,
    };

    return this.http.post<Division[]>(`${environment.APIURL}api/event/divisions/${eventId}/allowed`, params).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getDivisionAllowedPersons(divisionId: number): Observable<any> {
    return this.http.get<Division[]>(`${environment.APIURL}api/event/divisions/${divisionId}/self/allowed/persons`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getDivisionCompetitors(divisionId: number, competitors: any[]): Observable<any> {
    const params = {
      competitors,
    };

    return this.http.post<Division[]>(`${environment.APIURL}api/event/divisions/${divisionId}/self/allowed/persons`, params).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getUserLeagues(): Observable<any> {
    return this.http.get(`${environment.APIURL}api/leagues/my`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  private errorHandler(response: any) {
    this.zone.run(() => {
        this.toaster.error(response.message);
    });
  }

  private handleHttpError = (error: HttpErrorResponse): void => {
   return this.errorHandler(error);
  };

  private handleError = (
    error: HttpErrorResponse,
  ): Observable<any> => {
    const errorMessage = this.handleHttpError(error);
  
    return of({Errors: [errorMessage]});
  };

  postSountrackForMember(divisionId: number, personId: number, eventId: string, fileName: string, file: any) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append("eventId", eventId);
    formData.append("fileName", fileName);

    return this.http.post(`${environment.APIURL}api/event/divisions/${divisionId}/person/${personId}/music-file`, formData)
    .pipe(
      map((res) => res)
    );
  }

  getSountrackForMember(divisionId: number, personId: number, eventId: string): Observable<any> {
    return this.http.get(`${environment.APIURL}api/event/divisions/${divisionId}/person/${personId}/music-files?eventId=${eventId}`)
    .pipe(
      catchError(this.handleError),
      map((res) => res)
    );
  }

  deleteSountrackForMember(fileId: number) {
    return this.http.delete(`${environment.APIURL}api/event/divisions/music-file/${fileId}`);
  }



  uploadFileForUser(type: string, divisionId: number, eventId: string, fileName: string, file: any) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append("eventId", eventId);
    formData.append("fileName", fileName);
    formData.append("fileType", type);

    return this.http.post(`${environment.APIURL}api/event/divisions/${divisionId}/upload-file`, formData)
        .pipe(
            map((res) => res)
        );
  }

  getFileForUser(type: string, divisionId: number, eventId: string): Observable<any> {
    return this.http.get(`${environment.APIURL}api/event/divisions/${divisionId}/files?eventId=${eventId}&fileType=${type}`)
        .pipe(
            catchError(this.handleError),
            map((res) => res)
        );
  }

  deleteFileForUser(fileId: number) {
    return this.http.delete(`${environment.APIURL}api/event/divisions/file/${fileId}`);
  }

  public updateDivisionState(data: any[]): Observable<any> {
    return this.http.put(`${environment.APIURL}api/event/divisions/update-state`, data).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public moveRingDivisionsToUnassignedDivisions(data: any[]): Observable<any> {
    return this.http.put(`${environment.APIURL}api/event/divisions/move-to-unassigned`, data).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public updateBoutOrder(eventId: number, ringId: number, data: any[]): Observable<any> {
    return this.http.put(`${environment.APIURL}api/events/${eventId}/rings/${ringId}/reorder-bouts`, data).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public moveRoundsBouts(eventId: number, destinationRingId: number, data: any[]): Observable<any> {
    return this.http.put(`${environment.APIURL}api/events/${eventId}/rings/${destinationRingId}/move-rounds-bouts`, data).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public moveDivisionToUnassigned(ringDivisionId: number): Observable<any> {
    return this.http.put(`${environment.APIURL}api/event/divisions/${ringDivisionId}/move-to-unassigned`, {}).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public checkInCompetitors(divisionId: number, checkInMap: { [key: number]: boolean }, isStaging: boolean = false): Observable<any> {
    let apiUrl = `${environment.EVENT_ADMIN_URL}api/v1/editor/divisions/check-in`;
    let body = {
      divisionId: divisionId,
      checkInMap: checkInMap,
      stagingMode: isStaging,
      ringIds: null
    }
    return this.http.post<any>(apiUrl, body);
  }

  public filterParticipantReport(eventId: number, byDivision: boolean, search: string, ringId?: number, subEventId?: number): Observable<any> {
    let url = `${environment.ANALYTICIS_API_URL}api/statistics/rings/public/search/${eventId}`;

    const payload: any = {
      page: 0,
      size: 50,
      filter: {
        search: search
      }
    };

    if (ringId != null) {
      payload.filter['ringId'] = ringId;
    }

    if (byDivision !== undefined) {
      payload.filter['isDivisionSearch'] = byDivision;
    }

    if (subEventId !== undefined && subEventId > 0) {
      payload.filter['subEventId'] = subEventId;
    }

    return this.http.post<any>(url, payload).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  //public filterPublicScheduleParticipantReport(eventId: number, search: string, byDivision: boolean, ringId?: number): Observable<any> {
  public filterPublicScheduleParticipantReport(eventId: number, search: string, isShowCompleted?: boolean, byDivision?: boolean, eventParticipantId?: number, ringId?: number, countryId?: number, subEventId?: number, divisionId?: number): Observable<any> {
    //let url = `${environment.ANALYTICIS_API_URL}api/public/schedule/participant-report/filter/${eventId}?byDivision=${byDivision}`;
    // let url = `${environment.ANALYTICIS_API_URL}api/public/schedule/participant-report/filter/${eventId}`;
    let url = `${environment.ANALYTICIS_API_URL}api/statistics/rings/public/search/${eventId}`;

    const payload: any = {
      page: 0,
      size: 50,
      filter: {
        search: search
      }
    };

    if (eventParticipantId != null) {
      payload.filter['eventParticipantId'] = eventParticipantId;
    }

    if (ringId !== undefined) {
      payload.filter['ringId'] = ringId;
    }

    if (byDivision !== undefined) {
      payload.filter['isDivisionSearch'] = byDivision;
    }

    if (isShowCompleted !== undefined) {
      payload.filter['includeCompleted'] = isShowCompleted;
    }

    if (countryId !== undefined) {
      payload.filter['countryId'] = countryId;
    }

    if (subEventId !== undefined && subEventId > 0) {
      payload.filter['subEventId'] = subEventId;
    }

    if (divisionId !== undefined) {
      payload.filter['divisionId'] = divisionId;
    }

    return this.http.post<any>(url, payload).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public filterPublicScheduleCountries(eventId: number, ringId?: number): Observable<{id: number; name: string; code?: string;}[]> {
    let url = `${environment.ANALYTICIS_API_URL}api/statistics/rings/public/ring-statistics/${eventId}/countries`;
    const payload: any = {};
    if (ringId != null) {
      payload['ringId'] = ringId;
    }

    return this.http.post<any>(url, payload).pipe(
      catchError(this.handleError),
      map((res) => res)
    );
  }

  public filterPublicScheduleDivisionsByCountries(eventId: number, countryId: number, ringId?: number) {
    let url = `${environment.ANALYTICIS_API_URL}api/statistics/rings/public/ring-statistics/${eventId}/divisions`;
    const payload: any = {
      countryId: countryId
    };
    if (ringId != null) {
      payload['ringId'] = ringId;
    }

    return this.http.post<any>(url, payload).pipe(
      catchError(this.handleError),
      map((res) => res)
    );
  }

  public filterPublicScheduleRingsByCountries(eventId: number, countryId: number, ringId?: number) {
    let url = `${environment.ANALYTICIS_API_URL}api/statistics/rings/public/ring-statistics/${eventId}/rings`;
    const payload: any = {
      countryId: countryId
    };
    if (ringId != null) {
      payload['ringId'] = ringId;
    }

    return this.http.post<any>(url, payload).pipe(
      catchError(this.handleError),
      map((res) => res)
    );
  }

  public downloadPublicSchedulePDF(eventId: number, subEventId?: number, divisionId?: number) {
    const url = `${environment.ANALYTICIS_API_URL}api/downloads/public/brackets/${eventId}`;
    const body = {
      subEventId,
      divisionId
    };
    return this.http.post(url, body, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public sendDivisionNotification(data: SendNotificationDto): Observable<any> {
    let apiUrl = `${environment.EVENT_ADMIN_URL}api/v1/editor/divisions/send-notification`;

    return this.http.post<any>(apiUrl, data);
  }

  public getKataGroups(divisionId: number): Observable<any> {
    return this.http.get<any>(`${environment.APIURL}api/event/divisions/${divisionId}/kata-details`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getUnassignedDivisions(eventId: number, subEventId?: number): Observable<any[]> {
    const params: any = {};
    if (subEventId && subEventId > 0) {
      params['subEventId'] = subEventId;
    }

    return this.http.get(`${environment.APIURL}api/event/divisions/${eventId}/unassigned`, {
      params: params,
    }).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getMySubEvents(eventId: number): Observable<SubEvent[]> {
    return this.requestCache.get<SubEvent[]>(`${environment.APIURL}api/events/${eventId}/my/sub-events`, undefined, 300000).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicEventRings(eventId: number): Observable<Ring[]> {
    return this.http.get(`${environment.ANALYTICIS_API_URL}api/public/schedule/events/${eventId}/rings`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicEventRingGroups(eventId: number): Observable<RingGroup[]> {
    return this.http.get(`${environment.ANALYTICIS_API_URL}api/public/schedule/events/${eventId}/rings/groups`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicEventRingSessions(eventId: number, ringId: number): Observable<RingSession[]> {
    return this.http.get<RingSession[]>(`${environment.ANALYTICIS_API_URL}api/public/schedule/events/${eventId}/rings/${ringId}/sessions`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicDivisionsForEventRing(eventId: number, ringId: number): Observable<Division[]> {
    return this.http.get<Division[]>(`${environment.ANALYTICIS_API_URL}api/public/schedule/events/${eventId}/ring/${ringId}/divisions`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicDivisionParticipants(divisionId: number): Observable<DivisionParticipant[]> {
    return this.http.get<DivisionParticipant[]>(`${environment.ANALYTICIS_API_URL}api/public/schedule/division/${divisionId}/participants`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicSparringBracket(ringDivisionId: number, poolNo: number | null): Observable<any[]> {
    let queryParams: any = {};
    if (poolNo != null) {
      queryParams.poolNo = poolNo;
    }

    return this.http.get<any[]>(`${environment.ANALYTICIS_API_URL}api/public/schedule/division/${ringDivisionId}/bracket/sparring`, {
      params: queryParams,
    }).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicKataGroups(divisionId: number): Observable<any> {
    return this.http.get<any>(`${environment.ANALYTICIS_API_URL}api/public/schedule/division/${divisionId}/kata-details`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicFlowsSparring(eventId: number, ringId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.ANALYTICIS_API_URL}api/public/schedule/events/${eventId}/${ringId}/flows-sparring/`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getPublicUnassignedDivisions(eventId: number, subEventId?: number): Observable<any[]> {
    const params: any = {};
    if (subEventId && subEventId > 0) {
      params['subEventId'] = subEventId;
    }

    return this.http.get<any[]>(`${environment.ANALYTICIS_API_URL}api/public/schedule/divisions/${eventId}/unassigned`, {
      params: params,
    }).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public createRingTimeBreaks(eventId: number, ringTimeBreaks: any[]): Observable<any[]> {
    return this.http.post<any[]>(`${environment.APIURL}api/events/${eventId}/rings/time-break`, ringTimeBreaks).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public updateRingTimeBreaks(eventId: number, ringTimeBreaks: any[]): Observable<any[]> {
    return this.http.put<any[]>(`${environment.APIURL}api/events/${eventId}/rings/time-break`, ringTimeBreaks).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public deleteRingTimeBreak(eventId: number, breakId: number): Observable<any> {
    return this.http.delete(`${environment.APIURL}api/events/${eventId}/rings/time-break`, {
      body: [breakId]
    }).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public getRingTimeBreaksList(eventId: number, ringId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.APIURL}api/events/${eventId}/rings/${ringId}/time-break`).pipe(
        catchError(this.handleError),
        map((res) => res)
    );
  }

  public reassignParticipantToDivision(request: any): Observable<any> {
    return this.http.post<any>(`${environment.APIURL}api/event/divisions/reassign-participant`, request);
  }

  public reshuffleDivision(divisionId: number): Observable<any> {
    return this.http.put<any>(`${environment.EVENT_ADMIN_URL}api/v1/editor/divisions/${divisionId}/reshuffle`, {});
  }

  public buildDivisions(eventId: number, divisionIds: number[]): Observable<any> {
    const parameters = {
      eventId: eventId,
      divisionIds: divisionIds,
    };

    return this.http.put<any>(`${environment.EVENT_ADMIN_URL}api/v1/editor/divisions/build`, parameters);
  }

  public resetDivisionScore(divisionId: number): Observable<any> {
    return this.http.put<any>(`${environment.EVENT_ADMIN_URL}api/v1/editor/divisions/${divisionId}/reset-score`, {});
  }

  public getDivisionsForRings(eventId: number, data: any): Observable<any[]> {
    return this.http.put<any[]>(`${environment.APIURL}api/event/divisions/${eventId}/for-rings`, data);
  }

  public updateDivision(data: any): Observable<any> {
    return this.http.post<any>(`${environment.APIURL}api/event/divisions`, data);
  }

  public getDivision(eventId: number, divisionId: number): Observable<any> {
    return this.http.get<any>(`${environment.APIURL}api/event/divisions/${divisionId}/details`);
  }

  public checkPersonAsDivisionParticipant(divisionId: number, personId: number): Observable<boolean> {
    return this.http.get<boolean>(`${environment.APIURL}api/event/divisions/${divisionId}/person/${personId}/division-participant`);
  }

  public splitDivision(payload: any): Observable<any> {
    return this.http.put<any>(`${environment.APIURL}api/event/divisions/split`, payload);
  }

  public splitDivisionDryRun(payload: any): Observable<any> {
    return this.http.put<any>(`${environment.APIURL}api/event/divisions/split/dry-run`, payload);
  }

  public searchForTeamDivision(eventId: number, search: string): Observable<any> {
    const payload: any = {
      page: 0,
      size: 10,
      filter: {
        eventId: eventId,
        name: search
      }
    };

    return this.http.post<any>(`${environment.APIURL}api/event/divisions/teams/filter`, payload);
  }

  public checkTeamAlreadyAssignedToDivision(divisionId: number, teamId: number): Observable<boolean> {
    return this.http.get<boolean>(`${environment.APIURL}api/event/divisions/${divisionId}/team/${teamId}/check-assigned`);
  }

  public getDivisionsForTeam(eventId: number, teamId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.APIURL}api/event/divisions/${eventId}/team/${teamId}/divisions-list`);
  }

  public getTeamDetails(eventId: number, teamId: number): Observable<any> {
    return this.http.get<any>(`${environment.APIURL}api/event/divisions/${eventId}/team/${teamId}/details`);
  }

  public assignTeamToDivision(request: any): Observable<any> {
    return this.http.post<any>(`${environment.APIURL}api/event/divisions/assign-team`, request);
  }

  public getEventRingSessionsManageData(eventId: number, ringId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.APIURL}api/events/${eventId}/rings/${ringId}/sessions/manage`);
  }

  public manageRingSessions(eventId: number, ringSessionsDetails: any[]): Observable<any[]> {
    return this.http.post<any[]>(`${environment.APIURL}api/events/${eventId}/rings/sessions`, ringSessionsDetails);
  }

  removeRingSession(eventId: number, ringSession: any): Observable<boolean> {
    return this.http.delete<boolean>(`${environment.APIURL}api/events/${eventId}/rings/session/remove`, {body: ringSession});
  }

  public getEventStaffDetailsMap(eventId: number): Observable<any> {
    return this.http.get(`${environment.APIURL}api/events/${eventId}/staff/details`);
  }

  public sendEventStaffCredentials(eventId: number, data: any): Observable<boolean> {
    return this.http.post<boolean>(`${environment.APIURL}api/events/${eventId}/staff/send-credentials`, data);
  }

  public updateBracketPosition(divisionId: number, request: any): Observable<boolean> {
    return this.http.post<boolean>(`${environment.APIURL}api/event/divisions/${divisionId}/update-bracket-position`, request);
  }

}